import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';

/**
 * SystemEventContext: A global context for managing system event data
 * 
 * This context is responsible for:
 * 1. Storing all incoming system event data in a persistent cache
 * 2. Processing events in the background regardless of which component is visible
 * 3. Providing access to the latest metrics for all system components
 * 4. Notifying components when new data is available via custom events
 */

// Maximum number of data points to keep in chart history
const MAX_HISTORY_POINTS = 60;

// Create context
const SystemEventContext = createContext();

// Logger for SystemEventContext
const SELogger = {
  log: (...args) => console.log('[SystemEventContext]', ...args),
  warn: (...args) => console.warn('[SystemEventContext]', ...args),
  error: (...args) => console.error('[SystemEventContext]', ...args),
  debug: (...args) => console.debug('[SystemEventContext]', ...args)
};

/**
 * Provider component for SystemEvent data persistence
 * 
 * This allows metrics data to persist when switching tabs or panels, and ensures
 * that all events are processed in the background regardless of which component is visible.
 * 
 * @component
 */
export function SystemEventProvider({ children }) {
  // Persistent storage for metrics data by server ID
  const [metricsCache, setMetricsCache] = useState({});
  
  // Performance metrics to track processing
  const performanceMetrics = useRef({
    totalEventsProcessed: 0,
    lastProcessTime: 0,
    averageProcessTime: 0
  });
  
  // Store the last processed timestamp for each server to avoid duplicates
  const lastProcessedTimestamps = useRef({});
  
  // Flag to track if we've set up the global event listeners
  const listenersInitialized = useRef(false);
  
  /**
   * Helper function to extract system data from various event formats
   * 
   * This function handles different possible formats of the SystemEvent data
   * that might come from different sources or versions of the event producers.
   * 
   * @param {Object} eventData - The raw event data from WebSocket
   * @returns {Object|null} - The extracted system metrics or null if not found
   */
  const extractSystemData = (eventData) => {
    if (!eventData) {
      SELogger.warn('No event data provided to extractSystemData');
      return null;
    }
    
    // Handle different formats of SystemEvent data with detailed logging
    if (eventData.SystemEvent && typeof eventData.SystemEvent === 'object') {
      // Format 1: { SystemEvent: { cpu: {...}, memory: {...} } }
      return eventData.SystemEvent;
    } else if (eventData.event_data && typeof eventData.event_data === 'object') {
      // Format 2: { event_data: { cpu: {...}, memory: {...} } }
      return eventData.event_data;
    } else if (eventData.data && typeof eventData.data === 'object') {
      // Format 3: { data: { cpu: {...}, memory: {...} } }
      return eventData.data;
    } else {
      // Format 4: Direct format where the metrics are at the root level
      const hasSystemKeys = ['memory', 'cpu', 'disk', 'network'].some(key => 
        key in eventData && typeof eventData[key] === 'object'
      );
      
      if (hasSystemKeys) {
        return eventData;
      }
    }
    
    SELogger.warn('Failed to extract system data from event', eventData);
    return null;
  };
  
  /**
   * Update metrics for a specific server
   * 
   * This function processes incoming system events and updates the metrics cache.
   * It handles deduplication, data extraction, and notifies components of updates.
   * 
   * @param {string} serverId - The ID of the server to update
   * @param {Object} eventData - The raw event data from WebSocket
   * @param {string} eventTime - The timestamp of the event
   * @returns {boolean} - Whether the update was successful
   */
  const updateServerMetrics = useCallback((serverId, eventData, eventTime) => {
    if (!serverId || !eventData) return false;
    
    // Normalize server ID for consistent storage
    const normalizedServerId = serverId.toLowerCase();
    
    // Check for duplicate events to avoid redundant processing
    if (lastProcessedTimestamps.current[normalizedServerId] === eventTime) {
      return false;
    }
    
    // Update last processed timestamp
    lastProcessedTimestamps.current[normalizedServerId] = eventTime;
    
    // Extract system data from the event
    const systemData = extractSystemData(eventData);
    if (!systemData) {
      SELogger.warn(`Failed to extract system data from event for server ${normalizedServerId}`);
      return false;
    }
    
    // Format timestamp for display
    const timestamp = new Date(eventTime);
    const timeLabel = timestamp.toLocaleTimeString();
    
    // Create new metrics object with all the system metrics
    const newMetrics = {
      cpu: {
        percent: systemData.cpu?.percent || 0,
        count: systemData.cpu?.count || 0,
        perCpu: systemData.cpu?.per_cpu || []
      },
      memory: {
        total: systemData.memory?.total || 0,
        available: systemData.memory?.available || 0,
        used: systemData.memory?.used || 0,
        percent: systemData.memory?.percent || 0
      },
      disk: {
        total: systemData.disk?.total || 0,
        used: systemData.disk?.used || 0,
        free: systemData.disk?.free || 0,
        percent: systemData.disk?.percent || 0
      },
      network: {
        bytes_sent: systemData.network?.bytes_sent || 0,
        bytes_recv: systemData.network?.bytes_recv || 0,
        packets_sent: systemData.network?.packets_sent || 0,
        packets_recv: systemData.network?.packets_recv || 0
      },
      // Add timestamp for tracking
      eventTimestamp: timestamp.getTime()
    };
    
    // Update metrics cache with new data using functional state update pattern
    setMetricsCache(prevCache => {
      // Get existing server data or initialize new with default values
      const serverData = prevCache[normalizedServerId] || {
        currentMetrics: {
          cpu: { percent: 0, count: 0, perCpu: [] },
          memory: { total: 0, available: 0, used: 0, percent: 0 },
          disk: { total: 0, used: 0, free: 0, percent: 0 },
          network: { bytes_sent: 0, bytes_recv: 0, packets_sent: 0, packets_recv: 0 }
        },
        cpuHistory: [],
        memoryHistory: [],
        diskHistory: [],
        networkHistory: [],
        timeLabels: [],
        lastUpdateTime: 0,
        eventCount: 0,
        // Add lastNetworkMetrics to store previous readings for throughput calculation
        lastNetworkMetrics: null
      };
      
      // Calculate actual network throughput in MB/s based on delta between readings
      let networkThroughput = 0;
      
      // If we have previous network metrics, calculate the throughput rate
      if (serverData.lastNetworkMetrics) {
        const currentTime = newMetrics.eventTimestamp;
        const prevTime = serverData.lastNetworkMetrics.timestamp;
        
        // Calculate time difference in seconds between readings
        const timeDeltaSec = (currentTime - prevTime) / 1000;
        
        // Only calculate if we have a positive time delta to avoid division by zero
        if (timeDeltaSec > 0) {
          const prevSent = serverData.lastNetworkMetrics.bytes_sent;
          const prevRecv = serverData.lastNetworkMetrics.bytes_recv;
          const currSent = newMetrics.network.bytes_sent;
          const currRecv = newMetrics.network.bytes_recv;
          
          // Handle counter reset (e.g., after system reboot) - if current < previous, assume current is correct
          const bytesSentDelta = currSent >= prevSent ? currSent - prevSent : 0;
          const bytesRecvDelta = currRecv >= prevRecv ? currRecv - prevRecv : 0;
          
          // Calculate throughput in MB/s
          networkThroughput = (bytesSentDelta + bytesRecvDelta) / timeDeltaSec / 1024 / 1024;
          
          // Store calculated throughput rates in the metrics for UI display
          newMetrics.network.bytes_sent_rate = bytesSentDelta / timeDeltaSec;
          newMetrics.network.bytes_recv_rate = bytesRecvDelta / timeDeltaSec;
          newMetrics.network.total_bytes_rate = bytesSentDelta / timeDeltaSec + bytesRecvDelta / timeDeltaSec;
          
          // Calculate packet rates if needed
          const prevPacketsSent = serverData.lastNetworkMetrics.packets_sent;
          const prevPacketsRecv = serverData.lastNetworkMetrics.packets_recv;
          const currPacketsSent = newMetrics.network.packets_sent;
          const currPacketsRecv = newMetrics.network.packets_recv;
          
          const packetsSentDelta = currPacketsSent >= prevPacketsSent ? currPacketsSent - prevPacketsSent : 0;
          const packetsRecvDelta = currPacketsRecv >= prevPacketsRecv ? currPacketsRecv - prevPacketsRecv : 0;
          
          newMetrics.network.packets_sent_rate = packetsSentDelta / timeDeltaSec;
          newMetrics.network.packets_recv_rate = packetsRecvDelta / timeDeltaSec;
          newMetrics.network.total_packets_rate = packetsSentDelta / timeDeltaSec + packetsRecvDelta / timeDeltaSec;
        } else {
          // Time delta too small or invalid, set rates to zero
          newMetrics.network.bytes_sent_rate = 0;
          newMetrics.network.bytes_recv_rate = 0;
          newMetrics.network.total_bytes_rate = 0;
          newMetrics.network.packets_sent_rate = 0;
          newMetrics.network.packets_recv_rate = 0;
          newMetrics.network.total_packets_rate = 0;
        }
      } else {
        // First reading, no previous data to calculate delta
        newMetrics.network.bytes_sent_rate = 0;
        newMetrics.network.bytes_recv_rate = 0;
        newMetrics.network.total_bytes_rate = 0;
        newMetrics.network.packets_sent_rate = 0;
        newMetrics.network.packets_recv_rate = 0;
        newMetrics.network.total_packets_rate = 0;
      }
      
      // Update history arrays with new data
      const updatedServerData = {
        // Current snapshot of metrics
        currentMetrics: newMetrics,
        
        // Historical data for charts, efficiently sliced to maintain fixed size
        cpuHistory: [...serverData.cpuHistory, newMetrics.cpu.percent].slice(-MAX_HISTORY_POINTS),
        memoryHistory: [...serverData.memoryHistory, newMetrics.memory.percent].slice(-MAX_HISTORY_POINTS),
        diskHistory: [...serverData.diskHistory, newMetrics.disk.percent].slice(-MAX_HISTORY_POINTS),
        networkHistory: [...serverData.networkHistory, networkThroughput].slice(-MAX_HISTORY_POINTS),
        timeLabels: [...serverData.timeLabels, timeLabel].slice(-MAX_HISTORY_POINTS),
        
        // Store network metrics for next delta calculation
        lastNetworkMetrics: {
          bytes_sent: newMetrics.network.bytes_sent,
          bytes_recv: newMetrics.network.bytes_recv,
          packets_sent: newMetrics.network.packets_sent,
          packets_recv: newMetrics.network.packets_recv,
          timestamp: newMetrics.eventTimestamp
        },
        
        // Metadata for tracking and debugging
        lastUpdateTime: Date.now(),
        eventTime: timestamp.getTime(),
        eventCount: serverData.eventCount + 1
      };
      
      // Dispatch a custom event to notify components of the update
      const updateEvent = new CustomEvent('systemevent-store-updated', {
        detail: { 
          serverId: normalizedServerId, 
          metrics: updatedServerData,
          timestamp: Date.now(),
          eventTime: eventTime,
          sequenceNumber: updatedServerData.eventCount
        }
      });
      window.dispatchEvent(updateEvent);
      
      // Return updated cache
      return {
        ...prevCache,
        [normalizedServerId]: updatedServerData
      };
    });
    
    return true;
  }, []);
  
  /**
   * Get metrics for a specific server
   * 
   * @param {string} serverId - The ID of the server to get metrics for
   * @returns {Object|null} - The metrics object or null if not found
   */
  const getServerMetrics = useCallback((serverId) => {
    if (!serverId) return null;
    
    const normalizedServerId = serverId.toLowerCase();
    return metricsCache[normalizedServerId] || null;
  }, [metricsCache]);
  
  /**
   * Clear metrics for a specific server
   * 
   * @param {string} serverId - The ID of the server to clear metrics for
   */
  const clearServerMetrics = useCallback((serverId) => {
    if (!serverId) return;
    
    const normalizedServerId = serverId.toLowerCase();
    
    setMetricsCache(prevCache => {
      const newCache = { ...prevCache };
      delete newCache[normalizedServerId];
      return newCache;
    });
    
    // Also clear the timestamp reference to prevent deduplication issues
    if (lastProcessedTimestamps.current[normalizedServerId]) {
      delete lastProcessedTimestamps.current[normalizedServerId];
    }
    
    SELogger.log(`Cleared metrics for server ${normalizedServerId}`);
  }, []);
  
  /**
   * Clear all metrics data for all servers
   */
  const clearAllMetrics = useCallback(() => {
    setMetricsCache({});
    lastProcessedTimestamps.current = {};
    SELogger.log('Cleared all metrics data');
  }, []);
  
  /**
   * Get performance metrics for debugging and monitoring
   * 
   * @returns {Object} - Performance metrics for the SystemEventContext
   */
  const getPerformanceMetrics = useCallback(() => {
    return {
      ...performanceMetrics.current,
      activeServerCount: Object.keys(metricsCache).length,
      totalEventsStored: Object.values(metricsCache).reduce(
        (total, server) => total + (server.eventCount || 0), 0
      )
    };
  }, [metricsCache]);
  
  /**
   * Set up global event listeners to capture all system events
   * This ensures events are processed even when the SystemEventVisualizer is not visible
   */
  useEffect(() => {
    // Only set up listeners once
    if (listenersInitialized.current) return;
    
    // Handler for system events - this is the main entry point for all system events
    const handleSystemEvent = (event) => {
      if (event.detail && event.detail.serverId && event.detail.data) {
        const { serverId, data, timestamp } = event.detail;
        const eventTime = data.event_time || new Date(timestamp).toISOString();
        
        // Measure processing time
        const startTime = performance.now();
        
        // Process the event immediately regardless of which component is visible
        const success = updateServerMetrics(serverId, data, eventTime);
        
        // Update performance metrics
        if (success) {
          const endTime = performance.now();
          const processTime = endTime - startTime;
          
          performanceMetrics.current.totalEventsProcessed++;
          performanceMetrics.current.lastProcessTime = processTime;
          
          // Update rolling average process time
          const totalProcessed = performanceMetrics.current.totalEventsProcessed;
          const currentAvg = performanceMetrics.current.averageProcessTime;
          performanceMetrics.current.averageProcessTime = 
            (currentAvg * (totalProcessed - 1) + processTime) / totalProcessed;
          
          SELogger.debug(`Processed systemevent for server ${serverId} in ${processTime.toFixed(2)}ms`);
        }
      }
    };
    
    // Add global event listeners - we only need the systemevent-update listener
    window.addEventListener('systemevent-update', handleSystemEvent);
    
    // Mark as initialized
    listenersInitialized.current = true;
    
    SELogger.log('Global event listener initialized - processing all events in background');
    
    // Cleanup on unmount
    return () => {
      window.removeEventListener('systemevent-update', handleSystemEvent);
      SELogger.log('Global event listener removed');
    };
  }, [updateServerMetrics]);
  
  // Provide context values
  const contextValue = {
    getServerMetrics,
    updateServerMetrics,
    clearServerMetrics,
    clearAllMetrics,
    getPerformanceMetrics
  };
  
  return (
    <SystemEventContext.Provider value={contextValue}>
      {children}
    </SystemEventContext.Provider>
  );
}

/**
 * Custom hook to use the SystemEvent context
 */
export function useSystemEvent() {
  const context = useContext(SystemEventContext);
  if (!context) {
    throw new Error('useSystemEvent must be used within a SystemEventProvider');
  }
  return context;
}
